import { ListingCardInterests, PopularItemCardInterests } from 'app/components/listing-card-interests';
import { OnboardingProgress } from 'app/components/progress';
import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import { Grid } from 'app/design/primitives/grid';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { PlaceholderMedia } from 'app/overrides/rn-placeholder/PlaceholderMedia';
import { Shine } from 'app/overrides/rn-placeholder/animations/Shine';

import { IconArrowLeft, IconPlus, IconX } from '@tabler/icons-react-native';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useRoute } from 'app/hooks/navigation';
import { useDeviceHeight } from 'app/hooks/use-device-size';
import type { PopularItemsByTag } from 'app/network/category';
import type { ClosestListingsByTag, Tag } from 'app/network/listing';
import { useMyProfile } from 'app/network/profile';
import { createTheme as createDSTheme } from 'app/shopify';
import type { PopularItem } from 'app/types/category';
import type { SharerType } from 'app/types/preferences';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useUISize } from 'app/utils/web-helpers';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Platform, Pressable, ScrollView, View, type ViewStyle } from 'react-native';
import { ImageBackground } from 'expo-image';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { ModalTitle } from './components/modal-title';
import { INTERESTS_HORIZONTAL_GUTTER } from './interests-layout';
import { LoadingAvatar } from './interests-loading-avatar';
import { InterestsWebListingAggregate } from './interests-web-listing-aggregate';
import { InterestsStepName } from './interests-component.constants';
import type { TFunction } from 'i18next';

export { InterestsStepName } from './interests-component.constants';

const theme = createDSTheme();
const WEB_LISTING_CONTENT_CONTAINER_STYLE = {
  paddingHorizontal: INTERESTS_HORIZONTAL_GUTTER,
  paddingTop: theme.spacing['spacing.general.x2'],
};

export interface InterestsComponentProps {
  stepName: InterestsStepName;
  index: number;
  currentIndex: number;
  allTags: Tag[];
  selectedTags: string[];
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;
  selectedPopularItems: PopularItem[];
  setSelectedPopularItems: React.Dispatch<React.SetStateAction<PopularItem[]>>;
  selectedListings: string[];
  setSelectedListings: React.Dispatch<React.SetStateAction<string[]>>;
  sharerType?: SharerType;
  popularItemsByTag?: PopularItemsByTag[];
  closestOffersByTag?: ClosestListingsByTag[];
  isPending?: boolean;
  isTagsPending?: boolean;
  scrollToIndex: (index: number) => void;
  webAggregateListings?: boolean;
  webListingLayout?: 'small' | 'large';
  onWebAggregateClose?: () => void;
  headerTitle?: string;
  onHeaderClose?: () => void;
}

function InterestsScreenShell({
  isLargeUI,
  deviceHeight,
  insetBottom,
  stepName,
  children,
}: {
  isLargeUI: boolean;
  deviceHeight: number;
  insetBottom: number;
  stepName: InterestsStepName;
  children: React.ReactNode;
}) {
  return (
    <SafeAreaView
      edges={['top', 'bottom']}
      style={{
        width: '100%',
        ...(isLargeUI
          ? {
              flex: 1,
              minHeight: 0,
              borderBottomLeftRadius: theme.borderRadii['radius.xl'],
              borderBottomRightRadius: theme.borderRadii['radius.xl'],
              overflow: 'hidden' as const,
            }
          : { height: deviceHeight - insetBottom }),
        backgroundColor: theme.colors['surface.tertiary'],
        paddingBottom:
          stepName === InterestsStepName.ChooseTag || stepName === InterestsStepName.ChooseListing ? 80 : 0,
      }}
    >
      {children}
    </SafeAreaView>
  );
}

function InterestsListingHeader({
  currentStep,
  onBack,
  showProgress,
  totalSteps,
}: {
  currentStep: number;
  onBack: () => void;
  showProgress: boolean;
  totalSteps: number;
}) {
  const foreground = theme.colors['text.primary'];
  const { t } = useTranslation();

  return (
    <Row alignItems="center" minHeight={44} style={{ paddingHorizontal: INTERESTS_HORIZONTAL_GUTTER }}>
      <Pressable
        accessibilityLabel={t('common.back')}
        accessibilityRole="button"
        hitSlop={12}
        onPress={onBack}
        style={{ width: 28 }}
      >
        <IconArrowLeft strokeWidth={ICON_STROKE_WIDTH} color={foreground} />
      </Pressable>
      <View style={{ alignItems: 'center', flex: 1 }}>
        {showProgress ? <OnboardingProgress color="#1F2635" currentStep={currentStep} totalSteps={totalSteps} /> : null}
      </View>
      <View style={{ width: 28 }} />
    </Row>
  );
}

function ExplainStepHero({ isLargeUI }: { isLargeUI: boolean }) {
  return (
    <ImageBackground
      contentFit="cover"
      source={images.onboarding.interests}
      style={{
        width: '100%',
        alignSelf: 'stretch',
        height: isLargeUI ? 320 : 400,
        marginBottom: isLargeUI ? -48 : -60,
        zIndex: 1,
      }}
    />
  );
}

function InterestsStepTitle({
  stepName,
  sharerType,
  tagNameForTitle,
  explainTextGutter,
  marginTop,
}: {
  stepName: InterestsStepName;
  sharerType?: SharerType;
  tagNameForTitle: string;
  explainTextGutter: { paddingHorizontal: number } | undefined;
  marginTop: number;
}) {
  return (
    <Text
      textAlign={stepName === InterestsStepName.ChooseListing ? 'center' : undefined}
      variant="headerXSmall"
      style={{
        color: theme.colors['text.primary'],
        fontWeight: '700',
        fontSize: 36,
        lineHeight: 44,
        marginTop,
        ...explainTextGutter,
      }}
    >
      <Trans
        components={{ strong: <Text fontWeight="700" /> }}
        i18nKey={`${stepName}.title.${sharerType ?? ''}`}
        ns="interests"
        values={{ name: tagNameForTitle }}
      />
    </Text>
  );
}

function InterestsStepSubtitle({
  stepName,
  text,
  explainTextGutter,
}: {
  stepName: InterestsStepName;
  text: string;
  explainTextGutter: { paddingHorizontal: number } | undefined;
}) {
  return (
    <Text
      textAlign={stepName === InterestsStepName.ChooseListing ? 'center' : undefined}
      fontSize={18}
      style={{
        color: theme.colors['text.primary'],
        ...explainTextGutter,
      }}
    >
      {text}
    </Text>
  );
}

function InterestTagChip({
  tagName,
  selected,
  onToggle,
}: {
  tagName: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <Pressable
      onPress={onToggle}
      style={{
        padding: theme.spacing['spacing.general.x1'],
        paddingHorizontal: theme.spacing['spacing.general.x2'],
        borderRadius: theme.borderRadii['radius.xl'],
        backgroundColor: selected ? theme.colors['text.primary'] : 'white',
        marginVertical: theme.spacing['spacing.general.x0,5'],
      }}
    >
      <Row
        alignItems="center"
        style={{
          gap: theme.spacing['spacing.general.x1'],
          paddingRight: !selected ? 4 : 0,
          paddingLeft: selected ? 4 : 0,
        }}
      >
        {!selected ? <IconPlus strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} /> : null}
        <Text style={{ fontWeight: '700', color: selected ? 'white' : theme.colors['text.primary'] }}>{tagName}</Text>
        {selected ? <IconX strokeWidth={ICON_STROKE_WIDTH} size={24} color="white" /> : null}
      </Row>
    </Pressable>
  );
}

function InterestTagPicker({
  allTags,
  selectedTags,
  setSelectedTags,
}: {
  allTags: Tag[];
  selectedTags: string[];
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const selectedTagIds = new Set(selectedTags);
  return (
    <Row style={{ flexWrap: 'wrap', gap: theme.spacing['spacing.general.x1'] }}>
      {allTags.map((tag) => {
        const tagId = tag.id;
        const selected = selectedTagIds.has(tagId);
        return (
          <InterestTagChip
            key={tag.id}
            tagName={tag.tagName}
            selected={selected}
            onToggle={() => {
              if (selected) {
                setSelectedTags((prev) => prev.filter((selectedTag) => selectedTag !== tagId));
              } else {
                setSelectedTags((prev) => [...prev, tagId]);
              }
            }}
          />
        );
      })}
    </Row>
  );
}

function InterestTagPickerSkeleton() {
  const chipWidths = [96, 72, 132, 88, 116, 64, 140, 100, 76, 120, 92, 68];
  const chipHeight = theme.spacing['spacing.general.x1'] * 2 + 24;
  return (
    <Shine reverse={false}>
      <Row style={{ flexWrap: 'wrap', gap: theme.spacing['spacing.general.x1'] }}>
        {chipWidths.map((width) => (
          <PlaceholderMedia
            key={width}
            style={{ width, height: chipHeight, borderRadius: theme.borderRadii['radius.xl'] }}
          />
        ))}
      </Row>
    </Shine>
  );
}

function InterestsItemsGrid({
  isPending,
  currentIndex,
  profilePictureUri,
  popularItems,
  listings,
  selectedPopularItems,
  setSelectedPopularItems,
}: {
  isPending: boolean;
  currentIndex: number;
  profilePictureUri: string | number | undefined;
  popularItems: PopularItem[] | undefined;
  listings: ClosestListingsByTag['listings'] | undefined;
  selectedPopularItems: PopularItem[];
  setSelectedPopularItems: React.Dispatch<React.SetStateAction<PopularItem[]>>;
}) {
  return (
    <Grid columns={2} gap="spacing.general.x4">
      {isPending && currentIndex >= 2 ? (
        <View style={{ alignItems: 'center', width: '100%', marginTop: theme.spacing['spacing.general.x8'] }}>
          <LoadingAvatar uri={profilePictureUri} />
        </View>
      ) : null}
      {popularItems?.map((item) => (
        <PopularItemCardInterests
          key={item.id}
          popularItem={item}
          selectedPopularItems={selectedPopularItems}
          setSelectedPopularItems={setSelectedPopularItems}
        />
      ))}
      {listings?.map((listing) => (
        <ListingCardInterests key={listing.id} listing={listing} />
      ))}
    </Grid>
  );
}

function IosListBottomSpacer() {
  return Platform.OS === 'ios' ? <View style={{ height: 0 }} /> : null;
}

const getCurrentInterestData = ({
  allTags,
  closestOffersByTag,
  index,
  indexShift,
  popularItemsByTag,
  selectedTags,
}: Pick<InterestsComponentProps, 'allTags' | 'closestOffersByTag' | 'index' | 'popularItemsByTag' | 'selectedTags'> & {
  indexShift: number;
}) => {
  const currentTagId = selectedTags[index - indexShift];
  if (!currentTagId) {
    return { listingsForCurrentTag: undefined, popularItemsForCurrentTag: undefined, tagNameForTitle: '' };
  }
  return {
    listingsForCurrentTag: closestOffersByTag?.find((entry) => entry.tagId === currentTagId)?.listings,
    popularItemsForCurrentTag: popularItemsByTag?.find((entry) => entry.tagId === currentTagId)?.popularItems,
    tagNameForTitle: allTags.find((tag) => tag.id === currentTagId)?.tagName ?? '',
  };
};

const getInterestsPageLayout = ({
  defaultPagePadding,
  isLargeUI,
  showListingHeader,
  showTitleHeader,
  stepName,
}: {
  defaultPagePadding: ViewStyle;
  isLargeUI: boolean;
  showListingHeader: boolean;
  showTitleHeader: boolean;
  stepName: InterestsStepName;
}) => {
  const explainTextGutter =
    isLargeUI && stepName === InterestsStepName.Explain
      ? { paddingHorizontal: theme.spacing['spacing.general.x5'] }
      : undefined;
  const titleMarginTop =
    stepName === InterestsStepName.Explain
      ? theme.spacing['spacing.general.x10']
      : stepName === InterestsStepName.ChooseListing
        ? theme.spacing['spacing.general.x8']
        : theme.spacing['spacing.general.x4'];
  if (isLargeUI && stepName === InterestsStepName.Explain) {
    return {
      explainTextGutter,
      titleMarginTop,
      pagePaddingStyle: {
        ...defaultPagePadding,
        paddingTop: theme.spacing['spacing.general.x8'],
        paddingBottom: theme.spacing['spacing.general.x8'],
        paddingLeft: 0,
        paddingRight: 0,
      } as ViewStyle,
    };
  }
  return {
    explainTextGutter,
    titleMarginTop,
    pagePaddingStyle: {
      paddingBottom: defaultPagePadding.paddingBottom,
      paddingLeft: INTERESTS_HORIZONTAL_GUTTER,
      paddingRight: INTERESTS_HORIZONTAL_GUTTER,
      paddingTop: showListingHeader || showTitleHeader ? 0 : defaultPagePadding.paddingTop,
    } as ViewStyle,
  };
};

const InterestsTopHeader = ({
  currentStep,
  headerTitle,
  onBack,
  onHeaderClose,
  selectedTagCount,
  showListingHeader,
  showProgress,
  showTitleHeader,
}: {
  currentStep: number;
  headerTitle: string | undefined;
  onBack: () => void;
  onHeaderClose: (() => void) | undefined;
  selectedTagCount: number;
  showListingHeader: boolean;
  showProgress: boolean;
  showTitleHeader: boolean;
}) => {
  if (showListingHeader) {
    return <InterestsListingHeader {...{ currentStep, onBack, showProgress }} totalSteps={selectedTagCount} />;
  }
  if (!showTitleHeader) return null;
  return (
    <Column
      alignItems="center"
      justifyContent="space-between"
      marginBottom="spacing.general.x8"
      minHeight={44}
      style={{ paddingHorizontal: INTERESTS_HORIZONTAL_GUTTER }}
    >
      <ModalTitle close={onHeaderClose!} title={headerTitle} />
    </Column>
  );
};

const InterestsTitleAndSubtitle = ({
  explainTextGutter,
  marginTop,
  sharerType,
  showSubtitle,
  stepName,
  t,
  tagNameForTitle,
}: {
  explainTextGutter: { paddingHorizontal: number } | undefined;
  marginTop: number;
  sharerType: SharerType | undefined;
  showSubtitle: boolean;
  stepName: InterestsStepName;
  t: TFunction;
  tagNameForTitle: string;
}) => (
  <>
    <InterestsStepTitle {...{ explainTextGutter, marginTop, sharerType, stepName, tagNameForTitle }} />
    {showSubtitle ? (
      <InterestsStepSubtitle
        {...{ explainTextGutter, stepName }}
        text={t(`${stepName}.subtitle.${sharerType ?? ''}`)}
      />
    ) : null}
  </>
);

const InterestsTagsContent = ({
  allTags,
  isTagsPending,
  selectedTags,
  setSelectedTags,
}: Pick<InterestsComponentProps, 'allTags' | 'isTagsPending' | 'selectedTags' | 'setSelectedTags'>) =>
  isTagsPending && allTags.length === 0 ? (
    <InterestTagPickerSkeleton />
  ) : (
    <InterestTagPicker {...{ allTags, selectedTags, setSelectedTags }} />
  );

const InterestsStepContent = ({
  allTags,
  currentIndex,
  explainTextGutter,
  isPending,
  isTagsPending,
  listingsForCurrentTag,
  pagePaddingStyle,
  popularItemsForCurrentTag,
  profilePictureUri,
  selectedPopularItems,
  selectedTags,
  setSelectedPopularItems,
  setSelectedTags,
  sharerType,
  showSubtitle,
  stepName,
  t,
  tagNameForTitle,
  titleMarginTop,
}: Pick<
  InterestsComponentProps,
  | 'allTags'
  | 'currentIndex'
  | 'isPending'
  | 'isTagsPending'
  | 'selectedPopularItems'
  | 'selectedTags'
  | 'setSelectedPopularItems'
  | 'setSelectedTags'
  | 'sharerType'
  | 'stepName'
> & {
  explainTextGutter: { paddingHorizontal: number } | undefined;
  listingsForCurrentTag: ClosestListingsByTag['listings'] | undefined;
  pagePaddingStyle: ViewStyle;
  popularItemsForCurrentTag: PopularItem[] | undefined;
  profilePictureUri: string | number | undefined;
  showSubtitle: boolean;
  t: TFunction;
  tagNameForTitle: string;
  titleMarginTop: number;
}) => {
  const title = (
    <InterestsTitleAndSubtitle
      {...{ explainTextGutter, sharerType, showSubtitle, stepName, t, tagNameForTitle }}
      marginTop={titleMarginTop}
    />
  );
  const tags = <InterestsTagsContent {...{ allTags, isTagsPending, selectedTags, setSelectedTags }} />;
  if (Platform.OS === 'web' && stepName === InterestsStepName.ChooseTag) {
    return (
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={{ flex: 1, width: '100%' }}
        contentContainerStyle={pagePaddingStyle}
      >
        <Column style={{ gap: theme.spacing['spacing.general.x5'] }}>
          {title}
          {tags}
        </Column>
      </ScrollView>
    );
  }
  return (
    <Page isModal paddingStyle={pagePaddingStyle}>
      <Column style={{ gap: theme.spacing['spacing.general.x5'], height: '100%', width: '100%' }}>
        {title}
        {stepName === InterestsStepName.ChooseTag ? tags : null}
        <InterestsItemsGrid
          {...{ currentIndex, listings: listingsForCurrentTag, popularItems: popularItemsForCurrentTag }}
          isPending={Boolean(isPending)}
          profilePictureUri={profilePictureUri}
          selectedPopularItems={selectedPopularItems}
          setSelectedPopularItems={setSelectedPopularItems}
        />
        <IosListBottomSpacer />
      </Column>
    </Page>
  );
};

export function InterestsComponent({
  stepName,
  index,
  currentIndex,
  allTags,
  selectedTags,
  setSelectedTags,
  sharerType,
  selectedPopularItems,
  setSelectedPopularItems,
  popularItemsByTag,
  closestOffersByTag,
  isPending,
  isTagsPending,
  scrollToIndex,
  webAggregateListings,
  webListingLayout,
  headerTitle,
  onHeaderClose,
}: InterestsComponentProps) {
  const { t } = useScopedTranslation('interests');
  const route = useRoute<'interests'>();
  const indexShift = route.params.skipExplain ? 1 : 2;
  const profile = useMyProfile();

  const { listingsForCurrentTag, popularItemsForCurrentTag, tagNameForTitle } = getCurrentInterestData({
    allTags,
    closestOffersByTag,
    index,
    indexShift,
    popularItemsByTag,
    selectedTags,
  });

  const inset = useSafeAreaInsets();
  const deviceHeight = useDeviceHeight();
  const { isLargeUI } = useUISize();
  const defaultPagePadding = usePagePadding(true);

  const showSubtitle = !listingsForCurrentTag || listingsForCurrentTag.length > 0;
  const showListingHeader = stepName === InterestsStepName.ChooseListing;
  const showTitleHeader = Boolean(headerTitle && onHeaderClose) && !showListingHeader;
  const { explainTextGutter, pagePaddingStyle, titleMarginTop } = getInterestsPageLayout({
    defaultPagePadding,
    isLargeUI,
    showListingHeader,
    showTitleHeader,
    stepName,
  });
  const topHeader = (
    <InterestsTopHeader
      currentStep={index - indexShift}
      {...{ headerTitle, onHeaderClose, showListingHeader, showTitleHeader }}
      onBack={() => scrollToIndex(indexShift - 1)}
      selectedTagCount={selectedTags.length}
      showProgress={!webAggregateListings && selectedTags.length > 1}
    />
  );

  if (webAggregateListings && stepName === InterestsStepName.ChooseListing && webListingLayout) {
    return (
      <InterestsScreenShell
        deviceHeight={deviceHeight}
        insetBottom={inset.bottom}
        isLargeUI={isLargeUI}
        stepName={stepName}
      >
        {topHeader}
        <ScrollView
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator
          style={{ flex: 1, width: '100%' }}
          contentContainerStyle={WEB_LISTING_CONTENT_CONTAINER_STYLE}
        >
          <InterestsWebListingAggregate
            allTags={allTags}
            closestOffersByTag={closestOffersByTag}
            isPending={Boolean(isPending)}
            layout={webListingLayout}
            popularItemsByTag={popularItemsByTag}
            profilePictureUri={profile.data?.picture}
            selectedPopularItems={selectedPopularItems}
            selectedTags={selectedTags}
            setSelectedPopularItems={setSelectedPopularItems}
            sharerType={sharerType}
          />
          <IosListBottomSpacer />
        </ScrollView>
      </InterestsScreenShell>
    );
  }

  return (
    <InterestsScreenShell
      deviceHeight={deviceHeight}
      insetBottom={inset.bottom}
      isLargeUI={isLargeUI}
      stepName={stepName}
    >
      {topHeader}

      {stepName === InterestsStepName.Explain ? <ExplainStepHero isLargeUI={isLargeUI} /> : null}

      <InterestsStepContent
        {...{
          allTags,
          currentIndex,
          explainTextGutter,
          isPending,
          isTagsPending,
          listingsForCurrentTag,
          pagePaddingStyle,
          popularItemsForCurrentTag,
          selectedPopularItems,
          selectedTags,
          setSelectedPopularItems,
          setSelectedTags,
          sharerType,
          showSubtitle,
          stepName,
          t,
          tagNameForTitle,
          titleMarginTop,
        }}
        profilePictureUri={profile.data?.picture}
      />
    </InterestsScreenShell>
  );
}

export { LoadingAvatar } from './interests-loading-avatar';
